import { useState, useEffect, useCallback } from 'react';
import { api } from '../api.js';
import AddBoard from '../components/AddBoard.jsx';
import { timeAgo } from '../util/time.js';
import { Badge } from '../components/SourceBadge.jsx';
import HelpPopover from '../components/ui/HelpPopover.jsx';
import EmptyState from '../components/EmptyState.jsx';
import OpsCounters from '../components/OpsCounters.jsx';
import { toast } from '../components/Toasts.jsx';

// T140 — Sources management. Lists every ingestion adapter with its last-30-scans
// health and a per-source enable/disable toggle. Disabling stops the scanner
// from querying that source on the NEXT run — fully reversible, and it never
// deletes existing jobs or the applications you're tracking. Lives in the shared
// Layout shell; theme-safe (retargeted slate/brand/amber/emerald only).

const pctYield = (y) => (y == null ? '—' : `${Math.round(y * 100)}%`);

function Toggle({ enabled, busy, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={label}
      aria-busy={busy}
      disabled={busy}
      onClick={() => onChange(!enabled)}
      className={
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition disabled:opacity-50 ' +
        (enabled ? 'border-brand-600 bg-brand-600/40' : 'border-slate-700 bg-slate-800')
      }
    >
      <span
        className={'inline-block h-3.5 w-3.5 rounded-full bg-[var(--c-text-1)] transition ' + (enabled ? 'translate-x-4' : 'translate-x-0.5')}
      />
    </button>
  );
}

// What this source has produced, all-time: applications out of recommendations.
//
// Yield answers "is the adapter working". It cannot answer "is this board worth
// my morning", and on real data the two disagree loudly — the largest producer
// of recommendations in this system has an "ok" badge and has never produced a
// single application.
//
// `null` (never returned a job) reads as an em dash, not as 0/0: "I have never
// fetched anything here" is a different fact from "I fetched 370 and you applied
// to none", and only the second is an argument for turning it off.
function AppliedCell({ outcomes }) {
  if (!outcomes || !outcomes.recs) return <span className="text-slate-600">—</span>;
  const { applied, recs } = outcomes;
  const barren = applied === 0 && recs >= 25;
  return (
    <span
      className={barren ? 'text-amber-400' : 'text-slate-300'}
      title={
        barren
          ? `${recs} recommendations from this source and no application yet — worth asking whether it earns its place`
          : `${applied} of ${recs} recommendations from this source became applications`
      }
    >
      {applied}
      <span className="text-slate-600">/{recs}</span>
    </span>
  );
}

export default function SourcesPage() {
  const [sources, setSources] = useState(null);
  // T468 — removing a source means two different things, and the page says
  // which: a board HE added is deleted outright, a built-in adapter is
  // disabled and hidden. Both reversible, both counted rather than silently
  // shrinking the list.
  const [showHidden, setShowHidden] = useState(false);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [scanCount, setScanCount] = useState(0);
  const [counters, setCounters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null); // adapter name currently toggling

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      // tzOffset so the daily counters bucket on the operator's midnight, not
      // UTC's — a 23:30 scan in Copenhagen belongs to the day he saw it.
      const data = await api.get(
        `/sources?tzOffset=${-new Date().getTimezoneOffset()}${showHidden ? '&hidden=1' : ''}`,
      );
      setSources(data.sources);
      setHiddenCount(data.hiddenCount ?? 0);
      setScanCount(data.scanCount);
      setCounters(data.counters ?? null);
    } catch (e) {
      setErr(e?.body?.message || e?.message || 'Failed to load sources');
    } finally {
      setLoading(false);
    }
  }, [showHidden]);

  useEffect(() => {
    load();
  }, [load]);

  const setEnabled = useCallback(async (name, enabled) => {
    setBusy(name);
    setSources((prev) => prev?.map((s) => (s.name === name ? { ...s, enabled } : s))); // optimistic
    try {
      await api.patch(`/sources/${encodeURIComponent(name)}`, { enabled });
      toast(`${name} ${enabled ? 'enabled' : 'disabled'} — applies on the next scan`, { kind: 'success' });
    } catch (e) {
      setSources((prev) => prev?.map((s) => (s.name === name ? { ...s, enabled: !enabled } : s))); // revert
      toast(e?.body?.message || e?.message || 'Could not update source', { kind: 'error' });
    } finally {
      setBusy(null);
    }
  }, []);

  const removeSource = useCallback(async (row) => {
    setBusy(row.name);
    try {
      const r = await api.delete(`/sources/${encodeURIComponent(row.name)}`);
      toast(
        r?.removed
          ? `${row.name} removed`
          : `${row.name} hidden and disabled — "Show hidden" brings it back`,
        { kind: 'success' },
      );
      await load();
    } catch (e2) {
      toast(e2?.body?.message || e2?.message || 'Could not remove source', { kind: 'error' });
    } finally {
      setBusy(null);
    }
  }, [load]);

  const unhideSource = useCallback(async (name) => {
    setBusy(name);
    try {
      await api.post(`/sources/unhide/${encodeURIComponent(name)}`);
      await load();
    } catch (e2) {
      toast(e2?.body?.message || e2?.message || 'Could not unhide source', { kind: 'error' });
    } finally {
      setBusy(null);
    }
  }, [load]);

  const disabledCount = sources?.filter((s) => !s.enabled).length ?? 0;

  // T442 — the sources that have never once produced an application.
  //
  // `AppliedCell` already flags them one row at a time, which answers "is
  // this board earning its place" but not "how much are they costing me
  // together" — and the second question is the one that gets acted on.
  //
  // Two measurements shaped this, and both corrected a guess of mine.
  //
  // (1) I wrote in the roadmap that the barren sources had poured 30,159
  // jobs in. That was the whole corpus. On his actual open queue it is 133
  // rows of 850 — 16%, because the T220 relevance shelf already stops most
  // of it. Real, but not the big lever.
  //
  // (2) I assumed there were about five of them. Sources are per TENANT
  // (`ashby:9fin`, `greenhouse:gitlab`), so there are 38 — a list that long
  // is a wall, not a decision, and it sweeps in tenants he may well want to
  // keep watching. But the waste is concentrated: one source,
  // `hrmanager:statensrekruttering`, is 393 recommendations and zero
  // applications, 60% of the total on its own. Measured thresholds:
  //
  //   recs >= 5  → 12 sources, 590 recs (90% of the waste)
  //   recs >= 10 →  7 sources, 555 recs (85%)   ← chosen: readable and
  //   recs >= 20 →  4 sources, 515 recs (79%)      catches nearly all of it
  //
  // It lists and totals; it does not disable anything on its own. Which
  // sources run is his call.
  // T451 — what a source costs in DECISIONS, not whether it has ever
  // produced an application.
  //
  // T442 shipped this morning flagging "zero applications ever, with at
  // least 10 recommendations". Measuring the whole corpus afterwards
  // showed the better signal and the gap: decisions he actually made
  // (applied + dismissed) per application.
  //
  //   greenhouse       88 decisions per application   ← 1 of 65
  //   ashby            10
  //   it-jobbank        4.0    jobindex   3.5
  //   thehub            2.1    workable   2.0   teamtailor  1.8
  //   arbeitnow · hrmanager · smartrecruiters · remoteok · lever    ∞
  //
  // greenhouse escaped the old rule on a single application while costing
  // him 88 decisions for it — more than every productive source combined.
  // Measuring the thing he spends (attention) beats measuring the thing a
  // board produces (rows).
  const COSTLY_PER_APPLICATION = 10;
  const BARREN_MIN_RECS = 10;
  const costOf = (o) => {
    const decided = (o?.applied ?? 0) + (o?.dismissed ?? 0);
    if (!o?.applied) return decided >= BARREN_MIN_RECS || (o?.recs ?? 0) >= BARREN_MIN_RECS ? Infinity : 0;
    return decided / o.applied;
  };
  const barren = (sources ?? [])
    .filter((s) => s.enabled && costOf(s.outcomes) >= COSTLY_PER_APPLICATION)
    .sort((a, b) => costOf(b.outcomes) - costOf(a.outcomes) || b.outcomes.recs - a.outcomes.recs);
  const barrenRecs = barren.reduce((n, s) => n + (s.outcomes?.recs ?? 0), 0);

  const disableBarren = async () => {
    for (const s of barren) await setEnabled(s.name, false);
  };

  // T452 — the cost a FAMILY of sources carries, which no single row shows.
  //
  // Sources are per tenant: `greenhouse:databricks`, `greenhouse:cloudflare`,
  // fourteen of them. Measured across the corpus, greenhouse as a family cost
  // 88 decisions for ONE application — more than every productive source put
  // together — and not one of its tenants looks bad on its own. The line
  // above cannot see that by construction, because it reads one row at a
  // time.
  //
  //   family          tenants  recs  decisions  applications  cost
  //   arbeitnow             1   191        164             0     ∞
  //   greenhouse           14   116         88             1    88
  //   ashby                14    52         10             1    10
  //   jobindex              1   599         45            13     3
  //   thehub                1    41         15             7     2
  //
  // Only families with more than one tenant appear: for the rest this would
  // be the same number twice, under a heading that implies it is new.
  const families = (() => {
    const by = new Map();
    for (const s of sources ?? []) {
      const fam = String(s.name).split(':')[0];
      if (!by.has(fam)) by.set(fam, { fam, tenants: [], recs: 0, applied: 0, dismissed: 0, enabled: 0 });
      const f = by.get(fam);
      f.tenants.push(s.name);
      if (s.enabled) f.enabled += 1;
      f.recs += s.outcomes?.recs ?? 0;
      f.applied += s.outcomes?.applied ?? 0;
      f.dismissed += s.outcomes?.dismissed ?? 0;
    }
    return [...by.values()]
      .filter((f) => f.tenants.length > 1 && f.enabled > 0 && f.recs >= BARREN_MIN_RECS)
      .map((f) => ({ ...f, cost: costOf({ applied: f.applied, dismissed: f.dismissed, recs: f.recs }) }))
      .filter((f) => f.cost >= COSTLY_PER_APPLICATION)
      .sort((a, b) => b.cost - a.cost || b.recs - a.recs);
  })();

  const disableFamily = async (f) => {
    for (const name of f.tenants) await setEnabled(name, false);
  };

  // T268(7) — the header reported totals only ("72 sources · 3 disabled"), so
  // the single question this page exists to answer — is anything broken? — was
  // answered by scrolling a 72-row table and reading badges one at a time. The
  // rows are sorted worst-first server-side, which is invisible and therefore
  // no help. Same Badge component, aggregated, and each one filters the table.
  const byStatus = (sources ?? []).reduce((acc, s) => {
    const k = s.health?.status;
    if (k && k !== 'ok') acc[k] = (acc[k] ?? 0) + 1;
    return acc;
  }, {});
  const [statusFilter, setStatusFilter] = useState(null);
  const shown = statusFilter ? (sources ?? []).filter((s) => s.health?.status === statusFilter) : sources;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-100">Sources</h1>
        {sources && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
            {Object.entries(byStatus).map(([status, count]) => (
              <button
                key={status}
                type="button"
                onClick={() => setStatusFilter((cur) => (cur === status ? null : status))}
                aria-pressed={statusFilter === status}
                className={'rounded-full border-2 transition ' + (statusFilter === status ? 'border-brand-500' : 'border-transparent hover:opacity-80')}
              >
                <Badge status={status} count={count} />
              </button>
            ))}
            {statusFilter && (
              <button
                type="button"
                onClick={() => setStatusFilter(null)}
                className="underline decoration-dotted hover:text-slate-300"
              >
                show all
              </button>
            )}
            <span>
              <span className="text-slate-300">{sources.length}</span> sources ·{' '}
              <span className="text-slate-400">{disabledCount}</span> disabled · last {scanCount} scans
              {/* T468 — a hidden row is still counted. A list that quietly
                  shrinks is the thing this page exists to stop. */}
              {hiddenCount > 0 && !showHidden && (
                <>
                  {' · '}
                  <button
                    type="button"
                    onClick={() => setShowHidden(true)}
                    className="underline decoration-dotted transition hover:text-brand-400"
                  >
                    {hiddenCount} hidden
                  </button>
                </>
              )}
              {showHidden && (
                <>
                  {' · '}
                  <button
                    type="button"
                    onClick={() => setShowHidden(false)}
                    className="underline decoration-dotted transition hover:text-brand-400"
                  >
                    hide them again
                  </button>
                </>
              )}
            </span>
          </div>
        )}
      </header>

      <AddBoard onAdded={load} />

      <p className="flex flex-wrap items-center gap-1.5 text-sm text-slate-400">
        Turn a source off to stop scanning it. Reversible — it never deletes jobs you already have or applications
        you&apos;re tracking; it just stops fetching new postings from that board on the next scan.
        <HelpPopover label="What yield and the status badges mean" title="Yield &amp; source status">
          Yield = rows kept ÷ rows fetched. A low yield usually means the board is fine and your filters rejected it —
          a US-heavy careers page read by someone searching Denmark keeps almost nothing, and that is the filter
          working. So <strong>low yield</strong> is only flagged when rows also failed to parse, which is the case
          where the board changed under us. Badges: <strong>erroring</strong> (last scans threw),{' '}
          <strong>low yield</strong>, <strong>silent</strong> (no recent success), <strong>ok</strong>.
          {' '}<strong>Applied</strong> = applications ÷ recommendations this source has ever produced. It answers a
          different question from yield: a board can be perfectly healthy, send you hundreds of rows, and never once
          send you a job you applied to. Those are shown in amber.
        </HelpPopover>
      </p>

      {!loading && !err && counters && <OpsCounters counters={counters} />}

      {/* T442 — one line, because the per-row flag already exists and the
          only thing missing was the total and a single decision. */}
      {barren.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-amber-800/60 bg-amber-950/20 px-3 py-2">
          <span className="text-xs text-amber-300">
            {barren.length} {barren.length === 1 ? 'source costs' : 'sources cost'} more attention
            than {barren.length === 1 ? 'it returns' : 'they return'}
          </span>
          <span className="text-[11px] text-slate-400">
            {barren
              .map((s) => {
                const c = costOf(s.outcomes);
                return `${s.name} (${c === Infinity ? 'no applications' : `${c.toFixed(0)} decisions each`})`;
              })
              .join(', ')}{' '}
            · {barrenRecs} recommendations between them
          </span>
          <button
            type="button"
            disabled={Boolean(busy)}
            onClick={disableBarren}
            className="text-[11px] text-slate-400 underline decoration-dotted transition hover:text-amber-300 disabled:opacity-50"
          >
            Turn all {barren.length} off
          </button>
        </div>
      )}

      {/* T452 — the same question asked of a whole adapter family, because
          that is the level at which the answer is visible and the level at
          which turning it off means anything. */}
      {families.length > 0 && (
        <div className="mb-3 rounded-md border border-amber-800/60 bg-amber-950/20 px-3 py-2">
          <p className="text-xs text-amber-300">
            {families.length === 1 ? 'One board costs' : `${families.length} boards cost`} more across
            all their companies than any single one shows
          </p>
          <ul className="mt-1 space-y-0.5">
            {families.map((f) => (
              <li key={f.fam} className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-slate-400">
                <span className="font-mono text-slate-300">{f.fam}</span>
                <span>
                  {f.tenants.length} companies · {f.recs} recommendations ·{' '}
                  {f.cost === Infinity
                    ? `${f.applied} applications`
                    : `${f.cost.toFixed(0)} decisions per application`}
                </span>
                <button
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() => disableFamily(f)}
                  className="ml-auto underline decoration-dotted transition hover:text-amber-300 disabled:opacity-50"
                >
                  Turn off all {f.tenants.length}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {loading && <p className="text-sm text-slate-400">Loading…</p>}
      {err && !loading && <p className="text-sm text-red-400">{err}</p>}
      {!loading && !err && sources && sources.length === 0 && (
        <EmptyState
          icon="⚙"
          title="No sources registered"
          body="Adapters register themselves when the backend boots. An empty list means the backend has not started, or started without them — check the container logs."
          actions={[{ label: 'Reload', onClick: load, primary: true }]}
        />
      )}
      {!loading && !err && sources?.length > 0 && shown.length === 0 && (
        <EmptyState
          icon="◌"
          title={`No ${statusFilter} sources`}
          body="Nothing matches that status right now."
          actions={[{ label: 'Show all', onClick: () => setStatusFilter(null), primary: true }]}
        />
      )}

      {!loading && !err && sources && sources.length > 0 && (
        <>
          {/* desktop table */}
          <div className="hidden overflow-x-auto rounded-xl border border-slate-800 md:block">
            <table className="w-full text-xs">
              <thead className="bg-slate-900/60 text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th scope="col" className="px-3 py-2 text-left">Source</th>
                  <th scope="col" className="px-3 py-2 text-left">Status</th>
                  <th scope="col" className="px-3 py-2 text-right">Yield</th>
                  <th scope="col" className="px-3 py-2 text-right">Applied</th>
                  <th scope="col" className="px-3 py-2 text-right">Raw / kept</th>
                  <th scope="col" className="px-3 py-2 text-left">Last success</th>
                  <th scope="col" className="px-3 py-2 text-left">Last error</th>
                  <th scope="col" className="px-3 py-2 text-right">Enabled</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((s) => (
                  <tr
                    key={s.name}
                    className={'border-t border-slate-800/80 hover:bg-slate-900/40 ' + (s.enabled ? '' : 'opacity-50')}
                  >
                    <td className="px-3 py-1.5 font-mono text-slate-300">{s.name}</td>
                    <td className="px-3 py-1.5">
                      {s.operatorAdded ? (
                        <span className="text-[10px] text-slate-500">added by you</span>
                      ) : s.health ? (
                        <Badge status={s.health.status} />
                      ) : (
                        <span className="text-[10px] text-slate-600">no data</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums text-slate-400">{pctYield(s.health?.yield)}</td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                      <AppliedCell outcomes={s.outcomes} />
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums text-slate-400">
                      {s.health ? `${s.health.sumRaw} / ${s.health.sumFound}` : '—'}
                    </td>
                    <td className="px-3 py-1.5 text-slate-400">
                      {s.health?.lastSuccessAt ? timeAgo(s.health.lastSuccessAt) : <span className="text-slate-600">—</span>}
                    </td>
                    {/* The full text is the point of the column; truncated
                        with the rest in a title= it was mouse-only. */}
                    <td className="px-3 py-1.5 max-w-[16rem] text-slate-400">
                      {s.health?.lastError ? (
                        <details>
                          <summary className="cursor-pointer truncate">
                            {s.health.lastError}
                            {s.health.lastErrorAt && ` (${timeAgo(s.health.lastErrorAt)})`}
                          </summary>
                          <p className="mt-1 whitespace-pre-wrap break-words text-[11px] text-slate-500">{s.health.lastError}</p>
                        </details>
                      ) : (
                        '—'
                      )}
                      {/* T461 — "it stopped where it was told to" is a
                          different sentence from "it broke", so it gets a
                          different colour and does not count toward health.
                          15 of thehub's last 17 errors were this note. */}
                      {s.health?.lastNote && (
                        <p className="mt-1 break-words text-[11px] text-slate-500">{s.health.lastNote}</p>
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center justify-end gap-2">
                        {s.operatorAdded ? (
                          <span className="text-[10px] text-slate-600">—</span>
                        ) : (
                          <Toggle
                            enabled={s.enabled}
                            busy={busy === s.name}
                            onChange={(v) => setEnabled(s.name, v)}
                            label={`${s.enabled ? 'Disable' : 'Enable'} ${s.name}`}
                          />
                        )}
                        {/* T468 — the verb differs by what the row IS, and the
                            title says so rather than leaving him to find out:
                            a board he added is deleted, a built-in adapter is
                            hidden. Both come back. */}
                        {s.hidden ? (
                          <button
                            type="button"
                            disabled={busy === s.name}
                            onClick={() => unhideSource(s.name)}
                            className="rounded border border-slate-700 px-1.5 py-0.5 text-[10px] text-slate-400 transition hover:border-brand-600 hover:text-brand-400 disabled:opacity-40"
                          >
                            Unhide
                          </button>
                        ) : (
                          <button
                            type="button"
                            disabled={busy === s.name}
                            onClick={() => removeSource(s)}
                            title={
                              s.addedByOperator
                                ? `Delete ${s.name} — you added this board`
                                : `Hide ${s.name} and stop scanning it. "Show hidden" brings it back.`
                            }
                            className="rounded border border-slate-800 px-1.5 py-0.5 text-[10px] text-slate-500 transition hover:border-red-700 hover:text-red-400 disabled:opacity-40"
                          >
                            {s.addedByOperator ? 'Delete' : 'Hide'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* mobile cards */}
          <div className="space-y-2 md:hidden">
            {shown.map((s) => (
              <div
                key={s.name}
                className={'rounded-xl border border-slate-800 bg-slate-900/50 p-3 text-xs ' + (s.enabled ? '' : 'opacity-50')}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-slate-300">{s.name}</span>
                  <div className="flex items-center gap-2">
                    {s.operatorAdded ? (
                      <span className="text-[10px] text-slate-500">added by you</span>
                    ) : s.health ? (
                      <Badge status={s.health.status} />
                    ) : (
                      <span className="text-[10px] text-slate-600">no data</span>
                    )}
                    {!s.operatorAdded && (
                      <Toggle
                        enabled={s.enabled}
                        busy={busy === s.name}
                        onChange={(v) => setEnabled(s.name, v)}
                        label={`${s.enabled ? 'Disable' : 'Enable'} ${s.name}`}
                      />
                    )}
                  </div>
                </div>
                {s.health && (
                  <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-slate-500">
                    <dt>Yield</dt>
                    <dd className="text-right font-mono tabular-nums text-slate-400">{pctYield(s.health.yield)}</dd>
                    <dt>Applied</dt>
                    <dd className="text-right font-mono tabular-nums"><AppliedCell outcomes={s.outcomes} /></dd>
                    <dt>Raw / kept</dt>
                    <dd className="text-right font-mono tabular-nums">
                      {s.health.sumRaw} / {s.health.sumFound}
                    </dd>
                    <dt>Last success</dt>
                    <dd className="text-right">{s.health.lastSuccessAt ? timeAgo(s.health.lastSuccessAt) : '—'}</dd>
                  </dl>
                )}
                {/* T269(8) — the mobile card showed yield and counts but not
                    lastError, so on a phone a source could read "erroring" with
                    no way to find out why. */}
                {s.health?.lastError && (
                  <p className="mt-2 break-words text-[11px] text-amber-400">
                    {s.health.lastError}
                    {s.health.lastErrorAt && <span className="text-slate-500"> ({timeAgo(s.health.lastErrorAt)})</span>}
                  </p>
                )}
                {s.health?.lastNote && (
                  <p className="mt-1 break-words text-[11px] text-slate-500">{s.health.lastNote}</p>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
